import { useEffect, useState } from "react"
import PropTypes from "prop-types"
import classnames from "classnames"
import { useBgColorContext } from "./context"

import LimitedTextInput from "shared/limited_text_input"
import { headerColored } from "./utils/container_background_color"
import Spinner from "shared/components/spinner"
import LoaderDots from "shared/components/loader_dots"
import SwitchField from "shared/components/switch_field"
import ErrorMessageForField from "./error_message"

import { createApplet } from "./api"
import { formatCreateErrors } from "./utils/format_create_errors"

import styles from "./preview.module.scss"

export default function Preview({
  actions,
  appletTier,
  errors,
  filterCode,
  onCreationError,
  onCreationSuccess,
  preview,
  queries,
  trigger,
  userLogin,
  delay,
  userAtQuota,
  showAppletQuotaModal,
  upgradeWarningCopy,
  failedAppletRunNotificationsEnabled,
  onFailedAppletRunNotificationsChange,
}) {
  const bgColor = useBgColorContext()
  const [submitingApplet, updateSubmitingApplet] = useState(false)
  const [titleReady, updateTitleReady] = useState(false)
  const [titleLength, updateTitleLength] = useState(0)
  const titleLimit = 140

  useEffect(() => {
    titleLengthValidation(preview.name)
  }, [preview.name])

  useEffect(() => {
    headerColored(bgColor)

    return () => {
      headerColored("initial")
    }
  }, [bgColor])

  useEffect(() => {
    if (errors?.applet?.base) {
      window.appendFlash(errors.applet.base[0].message || "Could not create your Applet.", "danger")
    }
  }, [errors])

  const titleLengthValidation = title => {
    const length = title?.length || 0
    updateTitleLength(length)
    updateTitleReady(length > 0 && length <= titleLimit)
  }

  const handleTitleChange = value => {
    titleLengthValidation(value)
  }

  const onClick = e => {
    e.preventDefault()
    window.App.Utils?.logCustomDatadogAction("diy_applet_maker_preview_finish")

    if (userAtQuota) {
      return showAppletQuotaModal()
    } else {
      const description = document.querySelector("textarea[name='description']").value
      return onSubmit({ description })
    }
  }

  const onSubmit = async ({ description }) => {
    let response = {}
    let errors = {}

    try {
      updateSubmitingApplet(true)

      response = await createApplet({
        actions,
        description,
        filterCode,
        queries,
        serviceSlug: preview.serviceSlug,
        trigger,
        delay,
        failedAppletRunNotificationsEnabled,
      })

      errors = response?.diyAppletCreate?.errors

      const appletId = response?.diyAppletCreate?.normalized_applet?.id

      if (appletId) {
        onCreationSuccess?.()
        window.App.Utils?.logCustomDatadogAction?.("diy_applet_enabled", { applet_id: appletId })

        location.assign(`/applets/${appletId}`)
      } else {
        updateSubmitingApplet(false)

        if (errors !== null) {
          onCreationError(formatCreateErrors(errors))
        } else {
          onCreationError({
            applet: {
              base: "An unknown error occurred. Please try again later.",
            },
          })
        }
      }
    } catch (error) {
      updateSubmitingApplet(false)
      onCreationError({
        applet: { base: "An unknown error occurred. Please try again later." },
      })
    }
  }

  const serviceImages = () => {
    const steps = actions.map(step => step.service)
    steps.unshift(queries.map(step => step.service))
    steps.unshift(trigger.service)

    const reducer = (ac, step) => ({
      ...ac,
      [step.module_name]: {
        name: step.name,
        image: step.image,
      },
    })

    return steps.flat().reduce(reducer, {})
  }

  return (
    <>
      {Object.entries(preview).length === 0 ? (
        <Spinner />
      ) : (
        <>
          <div className={styles["preview-header"]} style={{ backgroundColor: preview.brandColor }}>
            <div className={styles.applet}>
              <div className={styles.services}>
                {Object.entries(serviceImages()).map(([module_name, entry]) => (
                  <img key={module_name} src={entry.image} title={entry.name} alt={entry.name} />
                ))}
              </div>

              <div className={styles["title-field"]}>
                <div className={styles["input-label"]}>
                  <span className={classnames("text-h4", styles.label)}>Applet Title</span>
                  <span className={classnames("body-small", styles.counter)}>
                    {titleLength} / {titleLimit}
                  </span>
                </div>

                <ErrorMessageForField errors={errors} field="applet.description" />

                <div className={styles["title-input"]}>
                  <LimitedTextInput
                    name="description"
                    value={preview.name}
                    onChange={handleTitleChange}
                    limit={titleLimit}
                    counterPosition="none"
                  />
                </div>

                <p className={classnames("body-small", styles.author)}>by {userLogin}</p>
              </div>
            </div>
          </div>

          <div className={styles.cta}>
            <div className={styles.settings}>
              <label className={styles.setting}>
                <span id="notify-on-failure-label" className="text-h5">
                  Notify me if this fails to run
                </span>
                <SwitchField
                  aria-labelledby="notify-on-failure-label"
                  checked={failedAppletRunNotificationsEnabled}
                  onChange={onFailedAppletRunNotificationsChange}
                />
              </label>
            </div>

            {userAtQuota && (
              <p className={classnames("body-medium", styles["upgrade-warning"])}>
                {upgradeWarningCopy.title}. {upgradeWarningCopy.copy}
              </p>
            )}

            <button
              type="submit"
              role="button"
              disabled={!titleReady || submitingApplet}
              onClick={onClick}
              className={classnames("button-primary", { [styles.submitting]: submitingApplet })}
              data-dd-action-name="diy_applet_maker_preview_finish"
            >
              {userAtQuota ? (
                "Upgrade"
              ) : submitingApplet ? (
                <>
                  Finishing
                  <LoaderDots className={styles["loader-dots"]} />
                </>
              ) : (
                "Finish"
              )}
            </button>
          </div>
        </>
      )}
    </>
  )
}

Preview.propTypes = {
  actions: PropTypes.array.isRequired,
  errors: PropTypes.object,
  filterCode: PropTypes.string.isRequired,
  onCreationError: PropTypes.func.isRequired,
  onCreationSuccess: PropTypes.func,
  preview: PropTypes.shape({
    brandColor: PropTypes.string,
    name: PropTypes.string,
    serviceSlug: PropTypes.string,
  }),
  queries: PropTypes.array.isRequired,
  trigger: PropTypes.object.isRequired,
  userLogin: PropTypes.string.isRequired,
  delay: PropTypes.number,
  failedAppletRunNotificationsEnabled: PropTypes.bool.isRequired,
  onFailedAppletRunNotificationsChange: PropTypes.func.isRequired,
  userAtQuota: PropTypes.bool,
  showAppletQuotaModal: PropTypes.func,
  upgradeWarningCopy: PropTypes.shape({
    title: PropTypes.string,
    copy: PropTypes.string,
  }),

}
